import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H3,
  H5,
} from "@sellar-io/components";
import { SupplierRelationshipEnumType } from "@sellar-io/api-v2/types";
import { useState } from "react";
import { Icon, ProfilePicture, SpinnerLegacy } from "../../components";
import { useBusinessUserList } from "../../hooks/api/v2/businessUser/useBusinessUserList";
import { useUserInviteList } from "../../hooks/api/v2/userInvite/useUserInviteList";
import { CustomerContactCreateModal } from "./CustomerContactCreateModal";
import { CustomerContactDeleteModal } from "./CustomerContactDeleteModal";

export const CustomerContacts = ({
  RetailerId,
  buyerName,
  connectionType,
}: {
  RetailerId: number;
  buyerName: string;
  connectionType: SupplierRelationshipEnumType;
}) => {
  const [customerContactCreateActive, setCustomerContactCreateActive] =
    useState<boolean>(false);

  const userInviteListQuery = useUserInviteList({
    query: [
      {
        value: RetailerId,
        type: "RetailerId",
        modifier: "EQUALS",
      },
    ],
  });

  const businessUsersQuery = useBusinessUserList({
    query: [
      {
        value: RetailerId,
        type: "RetailerId",
        modifier: "EQUALS",
      },
    ],
  });

  return (
    <>
      <Card className="bg-white">
        <CardHeader
          className={twMerge("bg-white", "flex items-center justify-between")}
        >
          <H3>Contacts</H3>
          <Button
            color={"secondary"}
            onClick={() => setCustomerContactCreateActive(true)}
          >
            <Icon icon={"PLUS"} />
            Add Contact
          </Button>
        </CardHeader>
        {(userInviteListQuery.isLoading &&
          userInviteListQuery.fetchStatus !== "idle") ||
        (businessUsersQuery.isLoading &&
          businessUsersQuery.fetchStatus !== "idle") ? (
          <SpinnerLegacy />
        ) : (
          <>
            {!!businessUsersQuery.data?.pages[0]?.length &&
              connectionType === "customer" && (
                <CardContent
                  className={"border-b border-solid border-gray-200 pb-4"}
                >
                  <div className={"flex flex-col gap-2"}>
                    <H5 className={"col-span-2 mb-2"}>
                      {businessUsersQuery.data.pages[0].length} active users at{" "}
                      {buyerName}
                    </H5>
                    {businessUsersQuery.data.pages[0].map((businessUser) => (
                      <BusinessUserRow
                        key={businessUser.id}
                        businessUser={businessUser}
                      />
                    ))}
                  </div>
                </CardContent>
              )}
            {!!userInviteListQuery.data?.pages[0]?.length && (
              <CardContent>
                <div className={"flex flex-col gap-2"}>
                  <H5 className={"mb-2"}>Your contacts at {buyerName}</H5>
                  {userInviteListQuery.data.pages[0].map((userInvite) => (
                    <UserInviteRow
                      key={userInvite.id}
                      userInvite={userInvite}
                    />
                  ))}
                </div>
              </CardContent>
            )}
          </>
        )}
      </Card>
      <CustomerContactCreateModal
        active={customerContactCreateActive}
        onDismiss={() => setCustomerContactCreateActive(false)}
        RetailerId={RetailerId}
      />
    </>
  );
};

const BusinessUserRow = ({
  businessUser,
}: {
  businessUser: {
    id: number;
    User: {
      name: string | null;
    };
  };
}) => {
  return (
    <div
      key={businessUser.id}
      className="flex flex-row items-center justify-between gap-2"
    >
      <div className={"flex items-center gap-2"}>
        <ProfilePicture name={businessUser.User.name ?? ""} />
        <H5>{businessUser.User.name}</H5>
      </div>
    </div>
  );
};

const UserInviteRow = ({
  userInvite,
}: {
  userInvite: { id: number; email: string };
}) => {
  const [deleteModalActive, setDeleteModalActive] = useState(false);

  return (
    <>
      <div className="flex justify-between">
        <div className={"flex items-center gap-2"}>
          <ProfilePicture name={userInvite.email} />
          <H5>{userInvite.email}</H5>
        </div>
        <div className={"flex items-center"}>
          <Button color={"tertiary"}>
            <Icon
              icon={"DELETE"}
              className={"cursor-pointer"}
              onClick={() => setDeleteModalActive(true)}
            />
          </Button>
        </div>
      </div>
      <CustomerContactDeleteModal
        active={deleteModalActive}
        userInviteId={userInvite.id}
        onDismiss={() => setDeleteModalActive(false)}
      />
    </>
  );
};
